import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  inject,
  Input,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

import { Observable } from 'rxjs';

import { TranslocoPipe } from '@jsverse/transloco';
import { NzSelectComponent, NzSelectModule } from 'ng-zorro-antd/select';

import { PlatformSelectOption } from './select.model';

type Value = unknown;

@Component({
  selector: 'ui-platform-select',
  templateUrl: './select.component.html',
  styleUrl: './select.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    NzSelectModule,
    FormsModule,
    TranslocoPipe
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiPlatformSelectComponent),
      multi: true
    }
  ]
})
export class UiPlatformSelectComponent implements AfterViewInit, ControlValueAccessor {
  readonly #changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  readonly #elementRef: ElementRef = inject(ElementRef);

  @Input() set options(options: (PlatformSelectOption | string | number)[]) {
    this.transformedOptions = options.map(option => {
      if (typeof option === 'string') {
        return {
          value: option,
          label: option,
          type: 'option'
        };
      }

      if (typeof option === 'number') {
        return {
          value: option,
          label: option.toString(),
          type: 'option'
        };
      }

      if (option === undefined || option === null) {
        return {
          value: '',
          label: '',
          type: 'option'
        };
      }

      if (option && typeof option.label === 'number') {
        option.label = (option.label as number).toString();
      }

      if (!option.type) {
        option.type = option.groupChildren ? 'group' : 'option';
      }
      return option;
    });
  }
  @Input() label = '';
  @Input() hint = '';
  @Input() placeholder = 'general.select-placeholder';
  @Input() autocomplete = false;
  @Input() disabled = false;
  @Input() borderless = false;
  @Input() showArrow = true;
  @Input() translatable = true;
  @Input() inputWideAsDropdown = false;
  @Input() set shouldAutoFocus(shouldAutoFocus: boolean) {
    if (shouldAutoFocus) {
      // wait for the component to be rendered
      setTimeout(() => {
        this.selectComponent?.focus();
        this.selectComponent?.setOpenState(true);
      });
    }
  }
  @Input() set shouldRecalculateWidth$(shouldRecalculateWidth$: Observable<any> | undefined | null) {
    // Used for example for language changes, in cooperation with inputWideAsDropdown.
    // If language was switched, we want to recalculate the width.
    shouldRecalculateWidth$?.subscribe(() => this.#updateHostWidth());
  }
  @HostBinding('class.inline') @Input() inline = false;
  @HostBinding('class.drawer') @Input() drawer = false;

  @Output() optionClicked: EventEmitter<Value> = new EventEmitter<Value>();

  @ViewChild('selectComponent', { read: NzSelectComponent }) selectComponent!: NzSelectComponent;
  @ViewChild('projectedDropdown') set projectedDropdown(projectedDropdown: ElementRef<HTMLDivElement> | undefined) {
    // projectedDropdown is true when it's rendered, which we control by setting this.calculationInProgress to true
    // There is no need to calculate anything if projectedDropdown is not rendered.
    // Also if select options (transformedOptions) are not set, there is no need for calculation.
    if (!projectedDropdown || !this.transformedOptions.length) {
      return;
    }

    // TODO: I leave it here for now because we decided to do it differently then before in a 1 minute call
    // We should think about desired functionality and based on that find a better solution than having "projected-dropdown"
    // in different components all over the app (with component that are expecting the projected-dropdown but are missing the functionality)
    // const calculatedDropdownWidth = this.#calculateProjectedDropdownWidth(projectedDropdown.nativeElement);
    // if (calculatedDropdownWidth !== this.selectComponent.cdkConnectedOverlay.minWidth) {
    //   this.selectComponent.cdkConnectedOverlay.minWidth = calculatedDropdownWidth;
    // }

    // if (this.inputWideAsDropdown) {
    //   this.inputWidth = `${this.selectComponent.cdkConnectedOverlay.minWidth}px`;
    // }

    this.calculationInProgress = false;
    this.#changeDetectorRef.detectChanges();
  }
  @ContentChild('selectOption', { static: false }) selectOptionTemplateRef: TemplateRef<any> | undefined;
  @ContentChild('autoOption', { static: false }) autoOptionTemplateRef: TemplateRef<any> | undefined;

  internalValue: Value = null;
  transformedOptions: PlatformSelectOption[] = [];

  calculationInProgress = false;
  inputWidth = '100%';

  ngAfterViewInit(): void {
    this.#updateHostWidth();
  }

  syncExternalValue: (value: Value) => void = () => {};
  syncExternalTouched: () => void = () => {};

  onModelChange(value: Value): void {
    this.syncExternalValue(value);
    this.syncExternalTouched();
    this.optionClicked.emit(value);
  }

  writeValue(value: Value): void {
    this.internalValue = value;
    this.#changeDetectorRef.detectChanges();
  }

  registerOnChange(fn: (value: Value) => void): void {
    this.syncExternalValue = (value: Value) => {
      fn(value);
    };
  }

  registerOnTouched(fn: () => void): void {
    this.syncExternalTouched = () => {
      fn();
    };
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.#changeDetectorRef.detectChanges();
  }

  openChanged(isOpen: boolean): void {
    if (isOpen) {
      this.calculationInProgress = true;
      this.#changeDetectorRef.detectChanges();
    }
  }

  #calculateProjectedDropdownWidth(projectedDropdownElement?: HTMLDivElement): number {
    if (!this.transformedOptions.length) {
      return this.selectComponent?.originElement?.nativeElement?.offsetWidth ?? 0;
    }
    const dropdownElement: HTMLDivElement | undefined = projectedDropdownElement ?? this.projectedDropdown?.nativeElement;
    const selectComponentWidth: number = this.selectComponent?.originElement?.nativeElement?.offsetWidth ?? 0;
    const projectedDropdownWidth = dropdownElement?.offsetWidth ?? 0;
    const { left, right, width } = this.#elementRef?.nativeElement.getBoundingClientRect();
    const pagePadding = 16; // TODO: 16 is padding, when we figure out proper variables sharing this will be changed to dynamic value
    const maxWidth = Math.max(left, window.innerWidth - right) + width - pagePadding;
    const idealDropdownWidth = Math.min(projectedDropdownWidth, maxWidth);

    return idealDropdownWidth && idealDropdownWidth > selectComponentWidth ? idealDropdownWidth : selectComponentWidth;
  }

  #updateHostWidth(): void {
    if (this.inputWideAsDropdown) {
      this.calculationInProgress = true;
      this.#changeDetectorRef.detectChanges();
    }
  }
}
